import React, { useState, useEffect } from 'react';
import api from '../api';

interface UsageReport {
  current_month: {
    put_requests: number;
    get_requests: number;
    storage_gb: number;
    estimated_cost: number;
  };
  free_tier_status: {
    put_requests_remaining: number;
    get_requests_remaining: number;
    storage_gb_remaining: number;
    put_requests_percentage: number;
    get_requests_percentage: number;
    storage_percentage: number;
  };
  alerts: Array<{
    type: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
  }>;
  recommendations: Array<{
    type: string;
    message: string;
    potential_savings: number;
  }>;
}

interface S3UsageMonitorProps {
  productId?: number;
}

const S3UsageMonitor: React.FC<S3UsageMonitorProps> = ({ productId }) => {
  const [usageData, setUsageData] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchUsageData = async (forceRefresh: boolean = false) => {
    try {
      setError(null);
      const url = forceRefresh 
        ? `/api/images/usage-report?_t=${Date.now()}` // Cache bust
        : '/api/images/usage-report';
      const response = await api.get(url);
      setUsageData(response.data);
    } catch (err: any) {
      console.error('Error fetching usage data:', err);
      setError(err.response?.data?.detail || 'Failed to fetch usage data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUsageData(true); // Force refresh
  };

  useEffect(() => {
    fetchUsageData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <div>Loading S3 usage data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '1rem' }}>
        <div style={{ color: '#dc3545', marginBottom: '1rem' }}>
          Error: {error}
        </div>
        <button 
          className="btn btn-secondary" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Retrying...' : 'Retry'}
        </button>
      </div>
    );
  }

  if (!usageData) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <div>No usage data available</div>
      </div>
    );
  }

  const getProgressBarColor = (percentage: number) => {
    if (percentage < 50) return '#28a745'; // Green
    if (percentage < 80) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4>S3 Usage Monitor</h4>
        <button 
          className="btn btn-secondary btn-sm" 
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Alerts */}
      {usageData.alerts && usageData.alerts.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {usageData.alerts.map((alert, index) => (
            <div 
              key={index}
              style={{
                padding: '0.5rem',
                marginBottom: '0.5rem',
                borderRadius: '4px',
                backgroundColor: alert.severity === 'error' ? '#f8d7da' : 
                                alert.severity === 'warning' ? '#fff3cd' : '#d1ecf1',
                color: alert.severity === 'error' ? '#721c24' : 
                       alert.severity === 'warning' ? '#856404' : '#0c5460',
                border: `1px solid ${alert.severity === 'error' ? '#f5c6cb' : 
                                    alert.severity === 'warning' ? '#ffeaa7' : '#bee5eb'}`
              }}
            >
              <strong>{alert.type}:</strong> {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Current Month Usage */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h5>Current Month Usage</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>Storage Used</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
              {(usageData.current_month?.storage_gb || 0).toFixed(2)} GB
            </div>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>PUT Requests</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
              {(usageData.current_month?.put_requests || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>GET Requests</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
              {(usageData.current_month?.get_requests || 0).toLocaleString()}
            </div>
          </div>
          <div style={{ padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>Estimated Cost</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1' }}>
              ${(usageData.current_month?.estimated_cost || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* Free Tier Status */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h5>AWS Free Tier Status</h5>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Storage */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Storage (5 GB limit)</span>
              <span>{(usageData.free_tier_status?.storage_percentage || 0).toFixed(1)}% used</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: '20px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(usageData.free_tier_status?.storage_percentage || 0, 100)}%`,
                height: '100%',
                backgroundColor: getProgressBarColor(usageData.free_tier_status?.storage_percentage || 0),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '0.25rem' }}>
              {(usageData.free_tier_status?.storage_gb_remaining || 0).toFixed(2)} GB remaining
            </div>
          </div>

          {/* PUT Requests */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>PUT Requests (2,000 limit)</span>
              <span>{(usageData.free_tier_status?.put_requests_percentage || 0).toFixed(1)}% used</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: '20px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(usageData.free_tier_status?.put_requests_percentage || 0, 100)}%`,
                height: '100%',
                backgroundColor: getProgressBarColor(usageData.free_tier_status?.put_requests_percentage || 0),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '0.25rem' }}>
              {(usageData.free_tier_status?.put_requests_remaining || 0).toLocaleString()} requests remaining
            </div>
          </div>

          {/* GET Requests */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>GET Requests (20,000 limit)</span>
              <span>{(usageData.free_tier_status?.get_requests_percentage || 0).toFixed(1)}% used</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: '20px', 
              backgroundColor: '#e9ecef', 
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${Math.min(usageData.free_tier_status?.get_requests_percentage || 0, 100)}%`,
                height: '100%',
                backgroundColor: getProgressBarColor(usageData.free_tier_status?.get_requests_percentage || 0),
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '0.25rem' }}>
              {(usageData.free_tier_status?.get_requests_remaining || 0).toLocaleString()} requests remaining
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {usageData.recommendations && usageData.recommendations.length > 0 && (
        <div>
          <h5>Cost Optimization Recommendations</h5>
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {usageData.recommendations.map((rec, index) => (
              <div 
                key={index}
                style={{
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {rec.type}
                </div>
                <div style={{ fontSize: '14px', marginBottom: '0.25rem' }}>
                  {rec.message}
                </div>
                {rec.potential_savings > 0 && (
                  <div style={{ fontSize: '12px', color: '#28a745' }}>
                    Potential savings: ${rec.potential_savings.toFixed(2)}/month
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default S3UsageMonitor;